import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/defaultV2.min.css';
import { ReferralHelpNote } from '../components/ReferralHelpNote';
import { StatusBadge } from '../components/StatusBadge';
import { toRawBase64 } from '../lib/attachments';
import { referralStatusLabel } from '../lib/referralStatus';
import { OPEN_STATUS } from '../lib/referralTimeLimits';
import { parseReferralUpdateDescription, prependReferralUpdateType, } from '../lib/referralUpdateTypes';
import { getUnseenReferralUpdateIds, markReferralUpdatesSeen } from '../lib/seenReferralUpdates';
import { ensureIncidentLookup, namedValueLabel, } from '../services/incidentLookup';
import { createReferralUpdatedInfo, fetchAttachmentDownload, fetchReferralDetail, saveBase64File, } from '../services/referrals';
import { ensureSignInUserInfo } from '../services/signinUserInfo';
import { uploadReferralAttachment } from '../services/workflow';
function fileToRawBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(toRawBase64(String(reader.result ?? '')));
        };
        reader.onerror = () => {
            reject(reader.error ?? new Error(`Failed to read ${file.name}`));
        };
        reader.readAsDataURL(file);
    });
}
function formatDateTime(value) {
    if (value == null || value === '')
        return '—';
    const raw = String(value);
    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z';
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime()))
        return raw;
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
function userName(user) {
    if (!user)
        return '—';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.email || '—';
}
function personDisplayName(person) {
    const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
    return name || person.email || `Person ${person.id}`;
}
function surveyTitle(survey) {
    return typeof survey?.title === 'string' ? survey.title : '';
}
function answeredFieldCount(formData) {
    if (!formData)
        return 0;
    const seen = new Set();
    for (const [key, value] of Object.entries(formData)) {
        if (value == null || value === '' || (Array.isArray(value) && value.length === 0))
            continue;
        seen.add(key.includes('.') ? key.slice(key.lastIndexOf('.') + 1) : key);
    }
    return seen.size;
}
function formatFileSize(bytes) {
    if (bytes == null || !Number.isFinite(bytes) || bytes < 0)
        return '';
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function canDownloadAttachment(attachment) {
    return Boolean(attachment.downloadUrl?.trim());
}
function pickFormString(data, ...keys) {
    for (const key of keys) {
        const value = data[key];
        if (typeof value === 'string' && value.trim())
            return value.trim();
        if (typeof value === 'number' && Number.isFinite(value))
            return String(value);
    }
    return '';
}
function patientNameFromFormData(data) {
    const nested = data.patientName;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        const record = nested;
        const name = [record.firstName, record.lastName]
            .map((part) => (typeof part === 'string' ? part.trim() : ''))
            .filter(Boolean)
            .join(' ');
        if (name)
            return name;
    }
    return pickFormString(data, 'patientFullName', 'patient_name');
}
function isPsychPlusReferralResponse(form) {
    const title = `${form.formTitle ?? ''} ${surveyTitle(form.survey)}`.toLowerCase();
    if (title.includes('psychplus') && title.includes('referral'))
        return true;
    const data = form.formData ?? {};
    return ((typeof data.contactStatus === 'string' || typeof data.initialAppointmentId === 'number') &&
        (data.patientId != null || data.patientName != null || data.requestedTime != null));
}
function appointmentDedupeKey(patientId, requestedTime) {
    const id = patientId.trim().toLowerCase();
    const raw = requestedTime.trim();
    if (!raw)
        return `${id}|`;
    const ms = Date.parse(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
    const timeKey = Number.isNaN(ms) ? raw.toLowerCase() : new Date(ms).toISOString();
    return `${id}|${timeKey}`;
}
function extractScheduledAppointments(forms) {
    if (!forms?.length)
        return [];
    const appointments = [];
    const seen = new Set();
    for (const form of forms) {
        if (!isPsychPlusReferralResponse(form))
            continue;
        const data = form.formData ?? {};
        const patientId = pickFormString(data, 'patientId');
        const requestedTime = pickFormString(data, 'requestedTime');
        const dedupeKey = appointmentDedupeKey(patientId, requestedTime);
        if (seen.has(dedupeKey))
            continue;
        seen.add(dedupeKey);
        appointments.push({
            key: `appt-${form.id}`,
            patientId,
            contactStatus: pickFormString(data, 'contactStatus'),
            patientName: patientNameFromFormData(data),
            requestedTime,
            created: form.created,
        });
    }
    return appointments;
}
function ReadOnlySurvey({ form }) {
    const model = useMemo(() => {
        const m = new Model(form.survey);
        m.widthMode = 'responsive';
        m.data = form.formData ?? {};
        m.mode = 'display';
        m.showNavigationButtons = false;
        m.showCompletedPage = false;
        m.showProgressBar = 'off';
        m.questionsOnPageMode = 'singlePage';
        return m;
    }, [form]);
    return _jsx(Survey, { model: model });
}
function FormThumbnail({ tile, onOpen }) {
    const fieldCount = answeredFieldCount(tile.form.formData);
    return (_jsxs("button", { type: "button", className: "form-tile", onClick: onOpen, children: [_jsx("span", { className: "form-tile-icon", "aria-hidden": "true", children: _jsxs("svg", { width: "28", height: "28", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("path", { d: "M14 2v6h6" }), _jsx("path", { d: "M8 13h8M8 17h6" })] }) }), _jsxs("span", { className: "form-tile-body", children: [_jsx("span", { className: "form-tile-title", children: tile.title }), _jsx("span", { className: "form-tile-source", children: tile.source }), _jsxs("span", { className: "form-tile-meta", children: [fieldCount, " ", fieldCount === 1 ? 'field' : 'fields', " completed"] })] })] }));
}
function FormModal({ tile, onClose }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (_jsx("div", { className: "form-modal-overlay", onClick: onClose, role: "presentation", children: _jsxs("div", { className: "form-modal", role: "dialog", "aria-modal": "true", "aria-label": tile.title, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "form-modal-header", children: [_jsxs("div", { children: [_jsx("h2", { children: tile.title }), _jsx("span", { className: "form-modal-source", children: tile.source })] }), _jsx("button", { type: "button", className: "form-modal-close", onClick: onClose, "aria-label": "Close", children: "\u2715" })] }), _jsx("div", { className: "form-modal-body", children: _jsx(ReadOnlySurvey, { form: tile.form }) })] }) }));
}
function UpdateInspireModal({ description, error, submitting, adjusterName, onDescriptionChange, onSubmit, onClose, }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting)
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, submitting]);
    return (_jsx("div", { className: "form-modal-overlay", onClick: submitting ? undefined : onClose, role: "presentation", children: _jsxs("div", { className: "form-modal update-inspire-modal", role: "dialog", "aria-modal": "true", "aria-label": "Post Update", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "form-modal-header", children: [_jsxs("div", { children: [_jsx("h2", { children: "Post Update" }), adjusterName && (_jsxs("span", { className: "form-modal-source", children: ["From ", adjusterName] }))] }), _jsx("button", { type: "button", className: "form-modal-close", onClick: onClose, "aria-label": "Close", disabled: submitting, children: "\u2715" })] }), _jsxs("div", { className: "form-modal-body update-inspire-modal-body", children: [_jsx("label", { className: "detail-update-inspire-label", htmlFor: "update-inspire-description", children: "Update" }), _jsx("textarea", { id: "update-inspire-description", className: "detail-update-inspire-textarea", rows: 5, value: description, onChange: (e) => onDescriptionChange(e.target.value), placeholder: "Enter an update for this referral\u2026", disabled: submitting, autoFocus: true }), error && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: error })), _jsxs("div", { className: "detail-update-inspire-actions", children: [_jsx("button", { type: "button", className: "btn-ghost", disabled: submitting, onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", disabled: submitting || !description.trim(), onClick: onSubmit, children: submitting ? 'Posting…' : 'Post Update' })] })] })] }) }));
}
export function ReferralDetailPage() {
    const { id } = useParams();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTile, setActiveTile] = useState(null);
    const [personTypes, setPersonTypes] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [userId, setUserId] = useState(null);
    const [adjusterName, setAdjusterName] = useState('');
    const [newUpdateIds, setNewUpdateIds] = useState(() => new Set());
    const [downloadingId, setDownloadingId] = useState(null);
    const [downloadError, setDownloadError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const fileInputRef = useRef(null);
    const [updateFormOpen, setUpdateFormOpen] = useState(false);
    const [updateDescription, setUpdateDescription] = useState('');
    const [updateSubmitting, setUpdateSubmitting] = useState(false);
    const [updateError, setUpdateError] = useState('');
    useEffect(() => {
        ensureIncidentLookup()
            .then((lookup) => {
            setPersonTypes(lookup.personTypes);
            setStatuses(lookup.statuses);
        })
            .catch((err) => console.error('[incidentLookup] failed', err));
    }, []);
    useEffect(() => {
        let cancelled = false;
        ensureSignInUserInfo()
            .then((info) => {
            if (cancelled)
                return;
            if (typeof info.detail?.id === 'number') {
                setUserId(info.detail.id);
            }
            const first = typeof info.detail?.firstName === 'string' ? info.detail.firstName.trim() : '';
            const last = typeof info.detail?.lastName === 'string' ? info.detail.lastName.trim() : '';
            const full = [first, last].filter(Boolean).join(' ');
            setAdjusterName(full || (typeof info.detail?.email === 'string' ? info.detail.email : ''));
        })
            .catch((err) => console.error('[signinUserInfo] failed', err));
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        const referralId = Number(id);
        if (!Number.isFinite(referralId)) {
            setError('Invalid referral id.');
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError('');
        setNewUpdateIds(new Set());
        setUploadError('');
        setUploadSuccess('');
        fetchReferralDetail(referralId)
            .then((data) => {
            if (!cancelled)
                setDetail(data);
        })
            .catch((err) => {
            if (!cancelled)
                setError(err instanceof Error ? err.message : 'Failed to load referral.');
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [id]);
    const updateIdsKey = useMemo(() => (detail?.updatedInfos ?? []).map((u) => u.id).join(','), [detail?.updatedInfos]);
    useEffect(() => {
        if (!detail || userId == null)
            return;
        const updateIds = detail.updatedInfos.map((u) => u.id);
        const unseen = getUnseenReferralUpdateIds(userId, detail.id, updateIds);
        setNewUpdateIds(unseen);
        if (updateIds.length === 0)
            return;
        // Delay marking so the highlight sticks for this first view (and survives
        // React Strict Mode's immediate remount before the timeout fires).
        const timer = window.setTimeout(() => {
            markReferralUpdatesSeen(userId, detail.id, updateIds);
        }, 1000);
        return () => window.clearTimeout(timer);
    }, [detail, userId, updateIdsKey]);
    const personTypeLabel = (type) => namedValueLabel(personTypes, type, 'Type');
    async function handleAttachmentDownload(attachment) {
        const path = attachment.downloadUrl?.trim();
        if (!path) {
            setDownloadError(`No download path for ${attachment.name}.`);
            return;
        }
        setDownloadError('');
        setDownloadingId(attachment.id);
        try {
            if (/^https?:\/\//i.test(path)) {
                window.open(path, '_blank', 'noopener,noreferrer');
                return;
            }
            const file = await fetchAttachmentDownload(attachment.bucketName?.trim() || '', path, attachment.name);
            saveBase64File(file);
        }
        catch (err) {
            setDownloadError(err instanceof Error ? err.message : `Failed to download ${attachment.name}.`);
        }
        finally {
            setDownloadingId(null);
        }
    }
    async function handleAttachmentUpload(files) {
        if (!detail || detail.status !== OPEN_STATUS || !files?.length)
            return;
        const selected = Array.from(files);
        setUploadError('');
        setUploadSuccess('');
        setUploading(true);
        try {
            const outcomes = await Promise.allSettled(selected.map(async (file) => {
                const content = await fileToRawBase64(file);
                return uploadReferralAttachment({
                    caseId: detail.id,
                    fileName: file.name,
                    content,
                });
            }));
            const failed = outcomes.filter((o) => o.status === 'rejected').length;
            const uploaded = outcomes.length - failed;
            if (failed > 0) {
                const firstReject = outcomes.find((o) => o.status === 'rejected');
                const reason = firstReject?.reason instanceof Error
                    ? firstReject.reason.message
                    : 'Upload failed.';
                setUploadError(uploaded > 0
                    ? `Uploaded ${uploaded} of ${outcomes.length}; ${failed} failed. ${reason}`
                    : reason);
            }
            else {
                setUploadSuccess(uploaded === 1
                    ? '1 attachment uploaded.'
                    : `${uploaded} attachments uploaded.`);
            }
            const refreshed = await fetchReferralDetail(detail.id);
            setDetail(refreshed);
        }
        catch (err) {
            setUploadError(err instanceof Error ? err.message : 'Failed to upload attachment.');
        }
        finally {
            setUploading(false);
            if (fileInputRef.current)
                fileInputRef.current.value = '';
        }
    }
    async function handleUpdateInspireSubmit() {
        if (!detail)
            return;
        const description = updateDescription.trim();
        if (!description) {
            setUpdateError('Enter an update before submitting.');
            return;
        }
        const prefixed = prependReferralUpdateType(description, 'adjuster');
        setUpdateError('');
        setUpdateSubmitting(true);
        try {
            await createReferralUpdatedInfo(detail.id, prefixed);
            setUpdateDescription('');
            setUpdateFormOpen(false);
            const refreshed = await fetchReferralDetail(detail.id);
            setDetail(refreshed);
        }
        catch (err) {
            setUpdateError(err instanceof Error ? err.message : 'Failed to submit update.');
        }
        finally {
            setUpdateSubmitting(false);
        }
    }
    const formTiles = useMemo(() => {
        if (!detail)
            return [];
        const tiles = [];
        detail.issueForms?.forEach((form) => {
            if (form.id <= 0)
                return;
            if (answeredFieldCount(form.formData) === 0)
                return;
            tiles.push({
                key: `issue-${form.id}`,
                title: form.formTitle || surveyTitle(form.survey) || `Form ${form.id}`,
                source: 'Referral Form',
                form,
            });
        });
        detail.tasks?.forEach((task) => {
            task.attachedForms?.forEach((form) => {
                if (form.id <= 0)
                    return;
                if (answeredFieldCount(form.formData) === 0)
                    return;
                tiles.push({
                    key: `task-${task.id}-${form.id}`,
                    title: form.formTitle || surveyTitle(form.survey) || `Form ${form.id}`,
                    source: task.description || `Task ${task.id}`,
                    form,
                });
            });
        });
        return tiles;
    }, [detail]);
    const scheduledAppointments = useMemo(() => extractScheduledAppointments(detail?.issueForms), [detail]);
    return (_jsxs("div", { className: "page", children: [_jsxs("div", { className: "page-header", children: [_jsxs("div", { children: [_jsx(Link, { to: "/", className: "back-link", children: "\u2190 Back to existing referrals" }), _jsxs("h1", { children: ["Referral ", id] }), detail && (_jsxs("p", { className: "page-subtitle", children: ["Created ", formatDateTime(detail.created), " by ", userName(detail.createdByUser)] }))] }), detail && (_jsxs("div", { className: "detail-header-actions", children: [_jsx(StatusBadge, { status: detail.status, statuses: statuses }), detail.status === OPEN_STATUS && (_jsx(Link, { to: `/referrals/${detail.id}/edit`, className: "btn-secondary detail-update-inspire-btn", children: "Edit referral" }))] })), _jsx(ReferralHelpNote, {})] }), loading && _jsx("div", { className: "table-empty", children: "Loading referral\u2026" }), error && !loading && _jsx("div", { className: "table-empty", children: error }), detail && !loading && (_jsxs("div", { className: "referral-detail", children: [_jsxs("section", { className: "detail-card", children: [_jsx("h2", { children: "Summary" }), _jsxs("dl", { className: "detail-meta", children: [_jsxs("div", { children: [_jsx("dt", { children: "Referral ID" }), _jsx("dd", { className: "mono", children: detail.id })] }), _jsxs("div", { children: [_jsx("dt", { children: "Site ID" }), _jsx("dd", { className: "mono", children: detail.siteId })] }), _jsxs("div", { children: [_jsx("dt", { children: "Status" }), _jsx("dd", { children: referralStatusLabel(detail.status, statuses) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Created" }), _jsx("dd", { children: formatDateTime(detail.created) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Last Updated" }), _jsx("dd", { children: formatDateTime(detail.updated) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Created By" }), _jsx("dd", { children: userName(detail.createdByUser) })] }), _jsxs("div", { children: [_jsx("dt", { children: "Owner" }), _jsx("dd", { children: detail.ownerName ? (_jsx("span", { title: detail.ownerId != null ? `ID ${detail.ownerId}` : undefined, children: detail.ownerName })) : detail.ownerId != null ? (_jsxs("span", { className: "mono", children: ["#", detail.ownerId] })) : ('—') })] })] })] }), _jsxs("section", { className: "detail-card", children: [_jsx("h2", { children: "Scheduled appointments" }), scheduledAppointments.length === 0 ? (_jsx("p", { className: "text-muted", children: "No scheduled appointments found for this referral." })) : (_jsx("ul", { className: "scheduled-appointments-list", children: scheduledAppointments.map((appointment) => (_jsx("li", { className: "scheduled-appointment", children: _jsxs("dl", { className: "detail-meta", children: [_jsxs("div", { children: [_jsx("dt", { children: "PsychPlus Patient Id" }), _jsx("dd", { className: "mono", children: appointment.patientId || '—' })] }), _jsxs("div", { children: [_jsx("dt", { children: "Contact status" }), _jsx("dd", { children: appointment.contactStatus || '—' })] }), _jsxs("div", { children: [_jsx("dt", { children: "Patient name" }), _jsx("dd", { children: appointment.patientName || '—' })] }), _jsxs("div", { children: [_jsx("dt", { children: "Requested date/time" }), _jsx("dd", { children: formatDateTime(appointment.requestedTime) })] })] }) }, appointment.key))) }))] }), _jsxs("section", { className: "detail-card", children: [_jsx("h2", { children: "People involved" }), (detail.personsInvolved?.length ?? 0) === 0 ? (_jsx("p", { className: "text-muted", children: "No people linked to this referral." })) : (_jsx("ul", { className: "persons-involved-badges", children: detail.personsInvolved.map((person) => {
                                    const typeLabel = personTypeLabel(person.type);
                                    const isPatient = personTypes.some((entry) => entry.value === person.type &&
                                        entry.name.trim().toLowerCase() === 'patient');
                                    const badgeClass = `person-badge${isPatient ? ' is-patient' : ''}`;
                                    const body = (_jsxs(_Fragment, { children: [_jsx("span", { className: "person-badge-name", children: personDisplayName(person) }), _jsx("span", { className: "person-badge-type", children: typeLabel })] }));
                                    return (_jsx("li", { children: person.email ? (_jsx("a", { className: badgeClass, href: `mailto:${person.email}`, title: person.email, children: body })) : (_jsx("span", { className: badgeClass, title: "No email", children: body })) }, person.id));
                                }) }))] }), _jsxs("section", { className: "detail-card detail-card--updates", children: [_jsxs("h2", { children: ["Updates", newUpdateIds.size > 0 && (_jsxs("span", { className: "referral-updates-new-count", children: [newUpdateIds.size, " new"] }))] }), (detail.updatedInfos?.length ?? 0) === 0 ? (_jsx("p", { className: "text-muted", children: "No updates for this referral." })) : (_jsx("ul", { className: "referral-updates-list", children: detail.updatedInfos.map((update) => {
                                    const isNew = newUpdateIds.has(update.id);
                                    const parsed = parseReferralUpdateDescription(update.description);
                                    const typeClass = parsed.type
                                        ? ` referral-update--${parsed.type.cssClass}`
                                        : '';
                                    return (_jsxs("li", { className: `referral-update${typeClass}${isNew ? ' is-new' : ''}`, children: [_jsxs("div", { className: "referral-update-meta", children: [_jsxs("span", { className: "referral-update-meta-left", children: [parsed.type && (_jsx("span", { className: "referral-update-type", children: parsed.type.shortLabel })), _jsx("time", { dateTime: update.occured || update.created || undefined, children: formatDateTime(update.occured || update.created) })] }), _jsxs("span", { className: "referral-update-meta-right", children: [isNew && _jsx("span", { className: "referral-update-new-badge", children: "New" }), _jsx("span", { className: "referral-update-by", children: userName(update.createdByUser) })] })] }), _jsx("p", { className: "referral-update-description", children: parsed.body || '—' })] }, update.id));
                                }) })), _jsx("div", { className: "referral-updates-actions", children: _jsx("button", { type: "button", className: "referral-post-update-btn", onClick: () => {
                                        setUpdateFormOpen(true);
                                        setUpdateError('');
                                    }, children: "Post Update" }) })] }), _jsxs("section", { className: "detail-card", children: [_jsx("h2", { children: "Forms" }), formTiles.length === 0 ? (_jsx("p", { className: "text-muted", children: "No forms attached to this referral." })) : (_jsx("div", { className: "form-tile-grid", children: formTiles.map((tile) => (_jsx(FormThumbnail, { tile: tile, onOpen: () => setActiveTile(tile) }, tile.key))) }))] }), _jsxs("section", { className: "detail-card", children: [_jsxs("div", { className: "detail-card-heading", children: [_jsx("h2", { children: "Attachments" }), detail.status === OPEN_STATUS && (_jsxs("div", { className: "detail-attachment-upload", children: [_jsx("input", { ref: fileInputRef, type: "file", id: "detail-attachment-file", className: "detail-attachment-file-input", multiple: true, disabled: uploading, onChange: (event) => void handleAttachmentUpload(event.target.files) }), _jsx("button", { type: "button", className: "btn-secondary detail-attachment-upload-btn", disabled: uploading, onClick: () => fileInputRef.current?.click(), children: uploading ? 'Uploading…' : 'Add attachment' })] }))] }), uploadError && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: uploadError })), uploadSuccess && !uploadError && (_jsx("p", { className: "detail-attachment-success", role: "status", children: uploadSuccess })), downloadError && (_jsx("p", { className: "detail-attachment-error", role: "alert", children: downloadError })), (detail.attachments?.length ?? 0) === 0 ? (_jsx("p", { className: "text-muted", children: detail.status === OPEN_STATUS
                                    ? 'No attachments yet. Add a file while this referral is Received.'
                                    : 'No attachments on this referral.' })) : (_jsx("ul", { className: "detail-attachment-list", children: detail.attachments.map((attachment) => {
                                    const downloadable = canDownloadAttachment(attachment);
                                    const busy = downloadingId === attachment.id;
                                    const sizeLabel = formatFileSize(attachment.size);
                                    const meta = [sizeLabel, formatDateTime(attachment.created)]
                                        .filter((part) => part && part !== '—')
                                        .join(' · ');
                                    return (_jsxs("li", { className: "detail-attachment-item", children: [_jsx("span", { className: "detail-attachment-icon", "aria-hidden": "true", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.6", children: _jsx("path", { d: "M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" }) }) }), _jsxs("span", { className: "detail-attachment-body", children: [downloadable ? (_jsx("button", { type: "button", className: "detail-attachment-name detail-attachment-link", disabled: busy || uploading, onClick: () => void handleAttachmentDownload(attachment), children: busy ? `Downloading ${attachment.name}…` : attachment.name })) : (_jsx("span", { className: "detail-attachment-name", children: attachment.name })), meta && _jsx("span", { className: "detail-attachment-meta", children: meta }), downloadable ? (_jsx("button", { type: "button", className: "detail-attachment-download", disabled: busy || uploading, onClick: () => void handleAttachmentDownload(attachment), children: busy ? 'Downloading…' : 'Download' })) : (_jsx("span", { className: "detail-attachment-meta", children: "No download path" }))] })] }, attachment.id));
                                }) }))] })] })), activeTile && _jsx(FormModal, { tile: activeTile, onClose: () => setActiveTile(null) }), updateFormOpen && (_jsx(UpdateInspireModal, { description: updateDescription, error: updateError, submitting: updateSubmitting, adjusterName: adjusterName, onDescriptionChange: setUpdateDescription, onSubmit: () => void handleUpdateInspireSubmit(), onClose: () => {
                    if (updateSubmitting)
                        return;
                    setUpdateFormOpen(false);
                    setUpdateError('');
                } }))] }));
}
